/* eslint-disable max-len */
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import styled, {css} from 'styled-components';
import ChapterCarousel from '../components/Comics/ChapterCarousel';
import ComicsChapterCard from '../components/Comics/ComicChapterCard';
import {loadComics} from '../store/actions/comics';
import {
    selectComics,
    selectPendingChapters,
    //selectComicsError,
    selectRecentChapters,
} from '../store/reducers';
import {
    breakpoints,
    mobileMediaQuery,
    notWebMediaQuery,
    useIsMobile,
} from '../styles';
import {getCurrentLang} from '../utils/i18n';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider from 'react-slick';
import Loader from '../components/Loader';
import {useWindowSize} from '../hooks/useWindowSize';
import {ReactComponent as ArrowIcon} from '../assets/arrow.svg';
import {Link} from 'react-router-dom';
import CarouselIndicator from '../components/CarouselIndicator';
import RecentReadView from '../components/Comics/RecentReadView';
import ComicBigItem from '../components/Comics/ComicBigItem';

const Comics = () => {
    const dispatch = useDispatch();

    const [cardWidth, setCardWidth] = useState(undefined);

    const isMobile = useIsMobile();
    const {width} = useWindowSize();
    const innerWidth = Math.min(width, 1440);

    let loadedComics = useSelector(selectComics);
    //let error = useSelector(selectComicsError);
    let recents = useSelector(selectRecentChapters);
    let continueReading = useSelector(selectPendingChapters);

    useEffect(() => {
        if (!loadedComics) {
            dispatch(loadComics());
        }
    }, [dispatch, loadedComics]);

    useEffect(() => {
        handleWindowResize({target: window});

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, [isMobile]);

    const {t} = useTranslation();

    const handleWindowResize = evt => {
        let width = Math.min(evt.target?.innerWidth, 1092);

        setCardWidth(width - (isMobile ? 50 : 80));
    };

    const buildRecents = recents => {
        if (isMobile) {
            return recents?.map((chapter, index) => (
                <ComicsChapterCard
                    chapter={chapter}
                    horizontal={false}
                    width={cardWidth}
                    height={216}
                    maskStart={index !== 0}
                    maskEnd={index !== recents.length - 1}
                    key={chapter.id.toString()}
                    index={index}
                />
            ));
        } else {
            return <ChapterCarousel items={recents} indexed={true} />;
        }
    };

    const {user} = useSelector(state => state);
    const idp = user && user.idp;
    const [sentPageview, setSentPageview] = useState(false);

    useEffect(() => {
        if (!sentPageview) {
            setSentPageview(true);

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    pageName: 'cnplus.com/comics',
                    section: 'comics',
                    subsection: 'comics',
                    contenttype: 'comics',
                    contenttitle: 'comics',
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: 'cnplus.com',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    userlang: getCurrentLang(),
                },
            });
        }

        // eslint-disable-next-line
    }, []);

    if (!loadedComics) {
        return (
            <Container direction="column">
                <Loader hide={false} />
            </Container>
        );
    }

    return (
        <Container direction="column">
            <Header comics={loadedComics} />
            {continueReading && continueReading.length > 0 && (
                <SizeLimiter>
                    <TextTitle mobile={isMobile}>
                        {t('comics.continueReading')}
                    </TextTitle>
                    <RecentReadView chapters={continueReading} maxItems={3} />
                </SizeLimiter>
            )}
            {recents && recents.length > 0 && (
                <SizeLimiter>
                    <TextTitle mobile={isMobile}>
                        {t('comics.newChapters')}
                    </TextTitle>
                    {buildRecents(recents)}
                </SizeLimiter>
            )}
            {loadedComics && loadedComics.length > 0 && (
                <SizeLimiter>
                    <TextTitle mobile={isMobile}>
                        {t('comics.allComics')}
                    </TextTitle>
                    <ComicGrid width={innerWidth}>
                        {loadedComics.map((comic, index) => (
                            <ComicBigItem comic={comic} key={comic.id} />
                        ))}
                    </ComicGrid>
                </SizeLimiter>
            )}
        </Container>
    );
};

const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: black;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    max-width: 1440px;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 392px);
`;

const TextTitle = styled.div`
    font-family: 'TTNormsProExtraBlack';
    font-style: normal;
    font-weight: 900;
    font-size: 20px;
    line-height: 24px;
    text-transform: uppercase;
    color: white;
    align-self: flex-start;
    margin: 24px 0px 6px ${props => (props.mobile ? '25px' : '40px')};
`;

const SizeLimiter = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 1440px;
    width: 100%;
    align-items: center;
`;

const ComicGrid = styled.div`
    display: grid;
    max-width: calc(100vw - 80px);
    column-gap: 20px;
    row-gap: 20px;
    grid-template-columns: 33% 33% 33%;
    width: ${props => props.width - 80}px;
    margin-top: 10px;

    @media screen and (min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.xl}px) {
        grid-template-columns: 50% 50%;
    }

    @media screen and (max-width: ${breakpoints.md}px) {
        grid-template-columns: 100%;
        max-width: calc(100vw - 50px);
        width: ${props => props.width - 50}px;
    }
`;

const Header = ({comics}) => {
    const {width} = useWindowSize();
    const {t} = useTranslation();
    const sliderRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);

    //calculate carousel height
    let cardWidth;
    if (width > 1024) {
        cardWidth = 315;
    } else if (width > 600) {
        cardWidth = width / 2.65;
    } else {
        cardWidth = width - 140;
    }

    let cardHeight = cardWidth * 1.41;
    let sliderHeight = cardHeight * 1.1;

    var settings = {
        dots: false,
        infinite: true,
        arrows: false,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: '0px',
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '30%',
                    slidesToShow: 1,
                },
            },
            {
                breakpoint: 600,
                settings: {
                    arrows: false,
                    centerMode: true,
                    centerPadding: '62px',
                    slidesToShow: 1,
                },
            },
        ],
    };
    //just to be safe
    let comicCopy = [...comics, ...comics, ...comics];

    const handlePreviousSlide = () => {
        // eslint-disable-next-line no-unused-expressions
        sliderRef.current?.slickPrev();
    };

    const handleNextSlide = () => {
        // eslint-disable-next-line no-unused-expressions
        sliderRef.current?.slickNext();
    };

    const onAfterChange = index => setCurrentSlide(index % comics.length);

    return (
        <HeaderContainer>
            <Slider
                {...settings}
                style={{height: sliderHeight}}
                ref={sliderRef}
                afterChange={onAfterChange}
            >
                {comicCopy.map((comic, index) => (
                    <div>
                        <HeaderImage
                            to={`comics/${comic.friendlyName}`}
                            url={`${comic.baseUrl}${comic.cover}`}
                            height={cardHeight}
                        />
                    </div>
                ))}
            </Slider>
            <CarouselIndicator selected={currentSlide} total={comics.length} />
            <LeftControls>
                <ButtonPrev disabled={false} onClick={handlePreviousSlide}>
                    <ArrowIcon title={t('controls.previous')} />
                </ButtonPrev>
            </LeftControls>

            <RightControls>
                <ButtonNext disabled={false} onClick={handleNextSlide}>
                    <ArrowIcon title={t('controls.next')} />
                </ButtonNext>
            </RightControls>
        </HeaderContainer>
    );
};

const HeaderImage = ({url, height, to}) => {
    return (
        <ImageContainer to={to}>
            <HeaderCover src={url} />
        </ImageContainer>
    );
};

const HeaderContainer = styled.div`
    align-items: center;
    width: 100%;
    justify-content: center;
    position: relative;
    max-width: 1000px;
    margin-top: 16px;

    ${mobileMediaQuery} {
        padding-bottom: 40px;
    }

    .slick-slide {
        -moz-transform: scale(0.9);
        -ms-transform: scale(0.9);
        -o-transform: scale(0.9);
        -webkit-transform: scale(0.9);
        transform: scale(0.9);
        transition: all 300ms ease;
    }

    .slick-center {
        -moz-transform: scale(1.08);
        -ms-transform: scale(1.08);
        -o-transform: scale(1.08);
        -webkit-transform: scale(1.08);
        transform: scale(1.08);
        transition: all 300ms ease;
    }
`;

const HeaderCover = styled.img`
    height: ${props => props.height}px;
                    margin: 16px 0;
                    transition: all 0.2s';
                    width: 90%;
`;

const ImageContainer = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0px;
`;

const LeftControls = styled.div`
    position: absolute;
    top: 50%;
    left: 0px;
    transform: translate(-50%, -50%);
    width: 0px;
    height: 0px;
    overflow: visible;
    z-index: 5;
`;

const RightControls = styled(LeftControls)`
    left: initial;
    right: 0px;
    transform: translate(-50%, -50%);
`;

const ButtonPrev = styled.button`
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 62px;
    height: 62px;
    border: none;
    border-radius: 5px;
    background-color: rgba(255, 255, 255, 0.7);
    box-sizing: border-box;
    cursor: pointer;
    padding: 0px;

    margin: auto;
    left: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 6px;
    svg {
        width: 18px;
        height: 28px;
        transform: scaleX(-1);
        path {
            fill: black;
        }
        margin: auto;
    }

    ${notWebMediaQuery} {
        display: none;
    }

    ${({disabled}) =>
        disabled &&
        css`
            cursor: initial;
            opacity: 0.65;
        `}
`;

const ButtonNext = styled(ButtonPrev)`
    padding-right: initial;
    padding-left: 6px;
    left: -16px;

    svg {
        transform: initial;
    }
`;

export default Comics;
